import { useDispatch, useSelector } from 'react-redux';
import { useState, useMemo, useRef } from 'react';
import _ from 'lodash';
import { reactToastify, TOASTIFY_TYPES } from 'common/utils/reactToastify';
import { getSelectorBasedStoreValue } from '../../redux/selector';
import { emptyQuantConfig, getQuantModes } from '../../configs/index';
import { onChangeInstrumentGroups } from '../../events/instrumentGroups';
import { setPartialQuantConfig } from '../../redux/actions';
import { SEGMENT_CONFIG } from 'common/constants';

export const MAX_INSTRUMENT_GROUPS = window?.isAdmin ? 20 : 2;

const getMaxInstrumentsGroups = (mode, instrumentGroups) => {
  return mode === getQuantModes.simple.value ? 1 : _.size(instrumentGroups) || 1;
};

const useInstrumentDetails = () => {
  const prevIntents = useRef({});
  const instrumentGroups = useSelector(
    (state) => getSelectorBasedStoreValue(state, 'quantConfig.instrumentGroups')
  );
  const maxInstrumentsInGroup = useSelector(
    (state) => getSelectorBasedStoreValue(state, 'quantConfig.maxInstrumentsInGroup')
  );
  const mode = useSelector(
    (state) => getSelectorBasedStoreValue(state, 'quantConfig.uiConfigs.mode')
  ) || getQuantModes.simple.value;

  const uiConfigs = useSelector(
    (state) => getSelectorBasedStoreValue(state, 'quantConfig.uiConfigs')
  ) || {};

  const pipeConfigs = useSelector((state) => getSelectorBasedStoreValue(state, 'quantConfig.pipeConfigs')) || [];
  const cases = useSelector((state) => getSelectorBasedStoreValue(state, 'quantConfig.cases')) || [];
  const updateIntents = [
    {
      data: cases,
      key: 'entry.intents',
    },
    {
      data: pipeConfigs,
      key: 'config.legs',
    },
  ]

  _.forEach(updateIntents, ({ data, key }) => {
    _.forEach(data, (item) => {
      const intents = _.get(item, key, []);
      _.forEach(intents, (intent) => {
        if (intent.instrument?.type === SEGMENT_CONFIG.option) {
          prevIntents.current[intent.identifier.label] = {
            instrumentGroup: _.map(instrumentGroups, (group) => {
              if (_.isEmpty(group)) return null;
              const instrumentIndex = intent.instrument.instrumentIndex;
              return group[instrumentIndex] || null;
            }),
            instrumentIndex: intent.instrument.instrumentIndex,
          };
        }
      });
    });
  });

  const [maxInstrumentGroups, setMaxInstrumentGroups] = useState(
    getMaxInstrumentsGroups(mode, instrumentGroups)
  );
  const isAdvancedMode = useMemo(() => mode === getQuantModes.advanced.value, [mode]);
  const dispatch = useDispatch();

  const onUpdateInstrumentGroups = (
    newInstrumentGroups, newMaxInstrumentInGroup, newUiConfigs = uiConfigs
  ) => {
    dispatch(
      setPartialQuantConfig(
        onChangeInstrumentGroups({
          instrumentGroups: newInstrumentGroups,
          cases,
          maxInstrumentsInGroup: Number(newMaxInstrumentInGroup),
          uiConfigs: newUiConfigs,
          pipeConfigs,
          prevIntents: prevIntents.current,
        })
      )
    );
  };

  const onUpdateInstrumentGroup = (newInstrumentGroup, idx) => {
    const newInstrumentGroups = _.cloneDeep(instrumentGroups);
    newInstrumentGroups[idx] = newInstrumentGroup;

    dispatch(
      setPartialQuantConfig(
        onChangeInstrumentGroups({
          instrumentGroups: newInstrumentGroups,
          cases,
          maxInstrumentsInGroup: Number(maxInstrumentsInGroup),
          uiConfigs,
          pipeConfigs,
          prevIntents: prevIntents.current,
        })
      )
    );
  };

  const onUpdateInstrument = (newInstrument, instrumentGroupIndex, instrumentIndex) => {
    const newInstrumentGroup = _.cloneDeep(_.get(instrumentGroups, instrumentGroupIndex, []));
    newInstrumentGroup[instrumentIndex] = newInstrument;
    onUpdateInstrumentGroup(newInstrumentGroup, instrumentGroupIndex);
  };

  const onDeleteInstrumentGroup = (instrumentGroupIndex) => {
    const newInstrumentGroups = _.cloneDeep(instrumentGroups);
    newInstrumentGroups.splice(instrumentGroupIndex, 1);
    setMaxInstrumentGroups(maxInstrumentGroups - 1);
    onUpdateInstrumentGroups(newInstrumentGroups, maxInstrumentsInGroup);
  };

  const onAddInstumentGroup = () => {
    if (maxInstrumentGroups < MAX_INSTRUMENT_GROUPS) {
      setMaxInstrumentGroups(maxInstrumentGroups + 1);
    } else {
      reactToastify('Maximum strategy limit exceeded', TOASTIFY_TYPES.INFO);
    }
  };

  const onChangeMode = (newMode) => {
    const newUiConfigs = { ...uiConfigs, mode: newMode };
    const flattenInstrumentGroups = _.flattenDeep(instrumentGroups);
    if (!_.isEmpty(flattenInstrumentGroups)) {
      return onUpdateInstrumentGroups([[_.head(flattenInstrumentGroups)]], 1, newUiConfigs);
    }
    if (newMode === getQuantModes.simple.value && _.isEmpty(flattenInstrumentGroups)) {
      return onUpdateInstrumentGroups(emptyQuantConfig().instrumentGroups, 1, newUiConfigs);
    }

    return onUpdateInstrumentGroups(instrumentGroups, maxInstrumentsInGroup, newUiConfigs);
  };

  return {
    instrumentGroups,
    maxInstrumentsInGroup,
    maxInstrumentGroups,
    isAdvancedMode,
    onUpdateInstrumentGroups,
    onUpdateInstrumentGroup,
    onUpdateInstrument,
    onDeleteInstrumentGroup,
    onAddInstumentGroup,
    onChangeMode,
    mode
  };
};

export default useInstrumentDetails;
